// Shared config for the different cable INSTALLATION SYSTEMS a circuit can
// belong to (power / KNX / BMA / …). One place owns the colours + i18n keys so
// every renderer (cards, plan markers, basis-of-estimate modal) stays in sync.
//
// The agent contract puts `system` on each BoQ item / circuit; "power" (or
// absent) is the normal low-voltage power case and must render exactly as today.
// The special systems get a DISTINCT accent so e.g. a Brandmeldeanlage (BMA)
// reads as a different installation, not just another UV circuit.

import type { CableSystem } from "../utils/fetchCables";

export type { CableSystem };

export interface SystemMeta {
  key: CableSystem;
  /** i18n key under smartSurveyor.cablesBeta.systems.* */
  labelKey: string;
  defaultLabelEn: string;
  defaultLabelDe: string;
  /** Tailwind bg-* class for a dot/badge fill. */
  dot: string;
  /** Tailwind text-* class for the accent text colour. */
  text: string;
  /** Tailwind bg-* (tinted) class for a badge background. */
  badgeBg: string;
  /** Tailwind border-* class for an accent border on the card. */
  accentBorder: string;
}

// DISTINCT colours per system. power = neutral slate (renders like today).
export const SYSTEM_META: Record<CableSystem, SystemMeta> = {
  power: {
    key: "power",
    labelKey: "smartSurveyor.cablesBeta.systems.power",
    defaultLabelEn: "Power",
    defaultLabelDe: "Starkstrom",
    dot: "bg-slate-400",
    text: "text-slate-600",
    badgeBg: "bg-slate-100",
    accentBorder: "border-slate-300",
  },
  knx: {
    key: "knx",
    labelKey: "smartSurveyor.cablesBeta.systems.knx",
    defaultLabelEn: "KNX",
    defaultLabelDe: "KNX",
    dot: "bg-teal-500",
    text: "text-teal-700",
    badgeBg: "bg-teal-50",
    accentBorder: "border-teal-400",
  },
  bma: {
    key: "bma",
    labelKey: "smartSurveyor.cablesBeta.systems.bma",
    defaultLabelEn: "Fire alarm",
    defaultLabelDe: "Brandmeldeanlage",
    dot: "bg-rose-600",
    text: "text-rose-700",
    badgeBg: "bg-rose-50",
    accentBorder: "border-rose-500",
  },
  media: {
    key: "media",
    labelKey: "smartSurveyor.cablesBeta.systems.media",
    defaultLabelEn: "Media (SAT/data)",
    defaultLabelDe: "Medien (SAT/Daten)",
    dot: "bg-violet-500",
    text: "text-violet-700",
    badgeBg: "bg-violet-50",
    accentBorder: "border-violet-400",
  },
  intercom: {
    key: "intercom",
    labelKey: "smartSurveyor.cablesBeta.systems.intercom",
    defaultLabelEn: "Intercom",
    defaultLabelDe: "Gegensprechanlage",
    dot: "bg-indigo-500",
    text: "text-indigo-700",
    badgeBg: "bg-indigo-50",
    accentBorder: "border-indigo-400",
  },
  safety: {
    key: "safety",
    labelKey: "smartSurveyor.cablesBeta.systems.safety",
    defaultLabelEn: "Safety",
    defaultLabelDe: "Sicherheit",
    dot: "bg-orange-500",
    text: "text-orange-700",
    badgeBg: "bg-orange-50",
    accentBorder: "border-orange-400",
  },
  earthing: {
    key: "earthing",
    labelKey: "smartSurveyor.cablesBeta.systems.earthing",
    defaultLabelEn: "Earthing / bonding",
    defaultLabelDe: "Erdung/Potentialausgleich",
    dot: "bg-amber-800",
    text: "text-amber-800",
    badgeBg: "bg-amber-100",
    accentBorder: "border-amber-700",
  },
};

/** Meta for a (possibly absent/unknown) system. Defaults to power. */
export function systemMeta(sys?: CableSystem | string | null): SystemMeta {
  if (sys && Object.prototype.hasOwnProperty.call(SYSTEM_META, sys)) {
    return SYSTEM_META[sys as CableSystem];
  }
  return SYSTEM_META.power;
}

/** True for anything that is NOT normal power (i.e. a distinct installation). */
export function isSpecialSystem(sys?: CableSystem | string | null): boolean {
  return !!sys && sys !== "power" && Object.prototype.hasOwnProperty.call(SYSTEM_META, sys);
}

// ── provenance.separate_systems enum-key labels ─────────────────────────────
// The token after "→" in a separate_systems entry is a stable enum key. It maps
// to a label independent of the SYSTEM_META colours (includes "no_cable").

export interface SeparateSystemLabel {
  key: string;
  defaultLabelEn: string;
  defaultLabelDe: string;
  /** i18n key under smartSurveyor.cablesBeta.separateSystemLabels.* */
  labelKey: string;
}

const SEPARATE_SYSTEM_LABELS: Record<string, { en: string; de: string }> = {
  bma: { en: "Fire alarm", de: "Brandmeldeanlage" },
  media: { en: "Media (SAT/data)", de: "Medien (SAT/Daten)" },
  earthing: { en: "Earthing / bonding", de: "Erdung/Potentialausgleich" },
  no_cable: { en: "No cable", de: "ohne Kabel" },
  knx: { en: "KNX", de: "KNX" },
  intercom: { en: "Intercom", de: "Gegensprechanlage" },
  safety: { en: "Safety", de: "Sicherheit" },
  power: { en: "Power", de: "Starkstrom" },
};

/** Label config for a separate_systems enum key (falls back to the raw key). */
export function separateSystemLabel(key: string): SeparateSystemLabel {
  const k = (key || "").trim();
  const hit = SEPARATE_SYSTEM_LABELS[k];
  return {
    key: k,
    labelKey: `smartSurveyor.cablesBeta.separateSystemLabels.${k}`,
    defaultLabelEn: hit?.en ?? k,
    defaultLabelDe: hit?.de ?? k,
  };
}

/** Split a provenance.separate_systems entry "text → enumKey" into its parts. */
export function parseSeparateSystem(entry: string): {
  text: string;
  systemKey: string;
} {
  const [before, after] = (entry || "").split("→");
  return {
    text: (before ?? "").trim(),
    systemKey: (after ?? "").trim(),
  };
}
